import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { Me } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { IconArrow } from "../components/Icons.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, CopyButton, Spinner } from "../components/ui.tsx";
import { fmt, rich, useT } from "../i18n/index.tsx";
import { ROLE_DESC } from "../lib/format.ts";
import { useSession } from "../lib/session.tsx";
import { AuthLayout, panelAccent } from "./AuthLayout.tsx";

type Lookup = { email: string; organization_name: string; role: string; existing_account: boolean };
type Enroll = { otpauth_uri: string; secret: string };

/** The token survives the trip through /login in sessionStorage (a redirect can drop the URL fragment). */
export const INVITE_KEY = "icn_invite_token";
const store = {
  get: () => { try { return sessionStorage.getItem(INVITE_KEY) ?? ""; } catch { return ""; } },
  set: (t: string) => { try { sessionStorage.setItem(INVITE_KEY, t); } catch { /* ignore */ } },
  clear: () => { try { sessionStorage.removeItem(INVITE_KEY); } catch { /* ignore */ } },
};

// One lookup per token per page load (React StrictMode runs effects twice in development, and the server rate-limits
// invite calls per IP).
const lookups = new Map<string, Promise<Lookup>>();
const lookupOnce = (token: string) => {
  let p = lookups.get(token);
  if (!p) {
    p = api.post<Lookup>("/invites/lookup", { token });
    lookups.set(token, p);
    p.catch(() => lookups.delete(token));
  }
  return p;
};

export function Invite() {
  const t = useT();
  // The token travels in the URL fragment, so it never reaches a server log.
  const [token] = useState(() => {
    const fromHash = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    if (fromHash) store.set(fromHash);
    return fromHash || store.get();
  });
  const [step, setStep] = useState<"loading" | "invalid" | "account" | "enroll" | "existing">(token ? "loading" : "invalid");
  const [invite, setInvite] = useState<Lookup | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [enroll, setEnroll] = useState<Enroll | null>(null);

  useEffect(() => {
    if (!token) return;
    lookupOnce(token)
      .then((r) => {
        setInvite(r);
        setStep(r.existing_account ? "existing" : "account");
      })
      .catch((e) => {
        if (isCode(e, "not_found")) store.clear();
        setLookupError(isCode(e, "not_found") ? null : errorMessage(e));
        setStep("invalid");
      });
  }, [token]);

  return (
    <AuthLayout
      panelTitle={rich(t.auth.invitePanelTitle, panelAccent)}
      panelPoints={t.auth.invitePanelPoints}
    >
      {step === "loading" && (
        <div className="auth-form center-block"><Spinner label={t.auth.checkingInvite} /></div>
      )}
      {step === "invalid" && (
        <div className="auth-form">
          <h1>{t.auth.invalidTitle}</h1>
          {lookupError ? (
            <Alert tone="error">{lookupError}</Alert>
          ) : (
            <p className="lead-sm">{t.auth.invalidBody}</p>
          )}
          <a href="/login" className="btn btn-ghost btn-block">{t.auth.goToLogIn}</a>
        </div>
      )}
      {step === "account" && invite && (
        <AccountStep
          token={token}
          invite={invite}
          onDone={(r) => {
            setEnroll(r);
            setStep("enroll");
          }}
        />
      )}
      {step === "enroll" && enroll && invite && <EnrollStep enroll={enroll} email={invite.email} />}
      {step === "existing" && invite && <ExistingAccount token={token} invite={invite} />}
    </AuthLayout>
  );
}

function Steps({ at }: { at: 1 | 2 }) {
  const t = useT();
  return (
    <ol className="steps-mini" aria-label={t.auth.progress}>
      <li className={at === 1 ? "current" : "done"} aria-current={at === 1 ? "step" : undefined}><span>1</span> {t.auth.stepAccount}</li>
      <li className={at === 2 ? "current" : ""} aria-current={at === 2 ? "step" : undefined}><span>2</span> {t.auth.stepMfa}</li>
    </ol>
  );
}

function AccountStep({ token, invite, onDone }: { token: string; invite: Lookup; onDone: (r: Enroll) => void }) {
  const t = useT();
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const pwErr = pw.length > 0 && pw.length < 12 ? t.auth.pwTooShort : pw.length > 128 ? t.auth.pwTooLong : null;
  const matchErr = touched && pw2 && pw !== pw2 ? t.auth.pwMismatch : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!name.trim()) return setError(t.auth.enterName);
    if (pw.length < 12 || pw.length > 128) return setError(t.auth.pwLength);
    if (pw !== pw2) return setError(t.auth.pwMismatch);
    setBusy(true);
    setError(null);
    try {
      onDone(await api.post<Enroll>("/invites/accept", { token, name: name.trim(), password: pw }));
    } catch (err) {
      setError(isCode(err, "not_found") ? t.auth.inviteUsed : errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <form className="auth-form" onSubmit={submit} noValidate>
      <Steps at={1} />
      <h1>{fmt(t.auth.joinOrg, { org: invite.organization_name })}</h1>
      <div className="invite-card">
        <div><span>{t.auth.email}</span><strong>{invite.email}</strong></div>
        <div><span>{t.auth.role}</span><strong>{builtInRole(t, invite.role)}</strong>{ROLE_DESC[invite.role.toLowerCase()] && <small>{ROLE_DESC[invite.role.toLowerCase()]}</small>}</div>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      <input type="email" name="username" autoComplete="username" value={invite.email} readOnly hidden />
      <div className="field">
        <label htmlFor="name">{t.auth.yourName}</label>
        <input id="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </div>
      <div className="field">
        <label htmlFor="pw">{t.auth.password}</label>
        <input id="pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} aria-describedby="pw-hint" aria-invalid={!!pwErr} />
        <p id="pw-hint" className={pwErr ? "field-error" : "field-hint"}>{pwErr ?? `${t.auth.pwHint} ${pw.length ? `(${pw.length})` : ""}`}</p>
      </div>
      <div className="field">
        <label htmlFor="pw2">{t.auth.confirmPassword}</label>
        <input id="pw2" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} onBlur={() => setTouched(true)} aria-invalid={!!matchErr} aria-describedby={matchErr ? "pw2-err" : undefined} />
        {matchErr && <p id="pw2-err" className="field-error">{matchErr}</p>}
      </div>
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
        {busy ? <Spinner /> : null} {t.common.continue} {!busy && <IconArrow size={16} />}
      </button>
    </form>
  );
}

function EnrollStep({ enroll, email }: { enroll: Enroll; email: string }) {
  const t = useT();
  const { setMe } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    import("qrcode")
      .then(({ default: QRCode }) => QRCode.toDataURL(enroll.otpauth_uri, { margin: 1, width: 360, errorCorrectionLevel: "M", color: { dark: "#0b1e46", light: "#ffffff" } }))
      .then(setQr)
      .catch(() => setQr(null));
  }, [enroll.otpauth_uri]);

  const grouped = enroll.secret.replace(/\s+/g, "").match(/.{1,4}/g)?.join(" ") ?? enroll.secret;

  const confirm = async (value = code) => {
    if (value.length !== 6) return setCodeError(t.auth.enterAppCode);
    setBusy(true);
    setCodeError(null);
    setError(null);
    try {
      const m = await api.post<Me>("/auth/enroll/confirm", { code: value });
      store.clear();
      setMe(m);
      toast(fmt(t.auth.welcomeToast, { org: m.organization.name }));
      navigate("/", { replace: true });
    } catch (err) {
      setBusy(false);
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.auth.codeRejectedEnroll);
      else setError(errorMessage(err));
    }
  };

  return (
    <form className="auth-form auth-form-wide" onSubmit={(e) => { e.preventDefault(); void confirm(); }} noValidate>
      <Steps at={2} />
      <h1>{t.auth.enrollTitle}</h1>
      <p className="lead-sm">{t.auth.enrollLead}</p>
      <div className="enroll">
        <div className="qr">
          {qr ? <img src={qr} alt={fmt(t.auth.qrAlt, { email })} width={180} height={180} /> : <Spinner />}
        </div>
        <div className="enroll-manual">
          <span className="field-label">{t.auth.cantScan}</span>
          <code className="secret-value">{grouped}</code>
          <CopyButton value={enroll.secret.replace(/\s+/g, "")} label={t.auth.copyKey} showText />
          <p className="field-hint">{fmt(t.auth.keyHint, { email })}</p>
        </div>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      <CodeInput label={t.auth.codeFromApp} value={code} onChange={(v) => { setCode(v); setCodeError(null); }} onComplete={(v) => void confirm(v)} error={codeError} disabled={busy} />
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
        {busy ? <Spinner /> : null} {t.auth.confirmAndFinish}
      </button>
    </form>
  );
}

/** The invitee already has an iCryptoNow login: log in as that e-mail, then join with just the token. */
function ExistingAccount({ token, invite }: { token: string; invite: Lookup }) {
  const t = useT();
  const { me, loading, setMe, logout } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sameUser = !!me && me.user.email.toLowerCase() === invite.email.toLowerCase();
  const toLogin = () => navigate(`/login?next=${encodeURIComponent("/invite")}&email=${encodeURIComponent(invite.email)}`);

  const join = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ joined: boolean; me: Me }>("/invites/accept", { token });
      store.clear();
      setMe(r.me);
      toast(fmt(t.auth.joinedToast, { org: r.me.organization.name }));
      navigate("/", { replace: true });
    } catch (err) {
      setBusy(false);
      if (isCode(err, "login_required", "unauthenticated")) toLogin();
      else setError(errorMessage(err));
    }
  };

  return (
    <div className="auth-form">
      <div className="eyebrow">{t.auth.invitation}</div>
      <h1>{fmt(t.auth.joinOrg, { org: invite.organization_name })}</h1>
      <div className="invite-card">
        <div><span>{t.auth.email}</span><strong>{invite.email}</strong></div>
        <div><span>{t.auth.role}</span><strong>{builtInRole(t, invite.role)}</strong></div>
      </div>
      {loading ? (
        <Spinner />
      ) : sameUser ? (
        <>
          <p className="lead-sm">{rich(t.auth.sameUserLead, {}, { email: me?.user.email ?? "", org: invite.organization_name })}</p>
          {error && <Alert tone="error">{error}</Alert>}
          <button type="button" className="btn btn-primary btn-block" onClick={() => void join()} disabled={busy}>
            {busy && <Spinner />} {fmt(t.auth.joinOrg, { org: invite.organization_name })}
          </button>
        </>
      ) : me ? (
        <>
          <Alert tone="warn">{rich(t.auth.wrongUser, {}, { current: me.user.email, invited: invite.email })}</Alert>
          <button type="button" className="btn btn-primary btn-block" onClick={async () => { await logout().catch(() => {}); toLogin(); }}>
            {fmt(t.auth.logOutAndLogInAs, { email: invite.email })}
          </button>
        </>
      ) : (
        <>
          <p className="lead-sm">{rich(t.auth.existingLead, {}, { email: invite.email, org: invite.organization_name })}</p>
          <button type="button" className="btn btn-primary btn-block" onClick={toLogin}>
            {t.auth.logInToJoin} <IconArrow size={16} />
          </button>
        </>
      )}
    </div>
  );
}

/** Built-in role names come from the server in English; show them in the console language (custom names as-is). */
function builtInRole(t: ReturnType<typeof useT>, role: string): string {
  return (t.common.roles as Record<string, string>)[role.toLowerCase()] ?? role;
}
